/* The figures are the cockpit's own per-category aggregation — the same one the Revenue & SOB
   table renders — run once per ended quarter instead of once over the reader's period. A second
   aggregation written here would answer the status with numbers the rest of the cockpit does not
   state, and the reader would have two truths to choose between. */
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { getUrlParams } from 'core-components/helpers/url'
import {
  FINANCIAL_SECTION_URL_VALUE,
  PLANNING_METRIC_URL_SEGMENT,
  PLANNING_SECTION_URL_SEGMENT,
  TAB_URL,
} from 'partner360/configs'
import {
  ATTENTION_STATUS_ACTIONS,
  ATTENTION_STATUS_BITS,
  getAttentionStatusRole,
  type TAttentionStatusRole,
} from 'partner360/entities/attentionStatuses'
import { normalizeFinancialMetricMode } from 'partner360/entities/financialMetricMode'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { PlanReviewDialog, type TPlanReviewRole } from 'partner360/features/planReview'
import { scrollToElement } from 'partner360/helpers/scrollToElement'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { formatCockpitMoney } from '../../../../lib'
import {
  selectCockpitMetric,
  selectCockpitPartnerData,
  selectCockpitUserAccess,
} from '../../../../model/selectors'
import type { CockpitPeriodKey } from '../../../../model/types'
import { AchievementScoreBadge } from '../../../components/achievementScoreBadge'
import { CockpitSectionHeader } from '../../../components/cockpitSectionHeader'
import { useCockpitSectionNavigation } from '../../../components/cockpitSectionNavigation'
import { CockpitTable, type ICockpitTableColumn } from '../../../components/cockpitTable'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import { useAttentionDetailOffering } from '../../hooks/useAttentionDetailOffering'
import { AttentionStatusActions } from '../attentionStatusActions'

import { type IQuarterPlanShortfall, useEndedQuarterShortfalls } from './useEndedQuarterShortfalls'

import styles from './AttentionStatusQuarterPlan.module.scss'

const DETAIL_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail
const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.quarterPlan
const REVENUE_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.revenueAndSob

/* The review dialog names its audiences with the commitment's own three words; the sheet's four
   roles map onto them, and everyone the sheet does not name reads the review as a manager does. */
const PLAN_REVIEW_ROLE_BY_STATUS_ROLE: Record<TAttentionStatusRole, TPlanReviewRole> = {
  pbm: 'hp',
  manager: 'manager',
  partner: 'partner',
  other: 'manager',
}

/* The three sentences the sheet's condition needs that `configs/translations.ts` does not carry
   yet: the quarter a row belongs to, the year that has not closed a quarter yet, and the offering
   whose ended quarters carry no figures at all. They are stated here, in the namespace's own
   shape, until they move into the config alongside the rest — the keys themselves are delivered
   to the translation service separately. `periodOpen`, `periodYtd` and `noData` retire with the
   period filter they described. */
const LOCAL_KEYS = {
  /** The column that names the ended quarter a row's plan and actuals were measured over. */
  quarter: 'jbp-financial-cockpit-attention-quarter-plan-quarter',
  /** Available param: {fy}. No quarter of the viewed year has closed, so nothing is scorable. */
  noEndedQuarters: 'jbp-financial-cockpit-attention-quarter-plan-no-ended-quarters',
  /** Available param: {period}. The ended quarters carry neither a plan nor an actual here. */
  noFigures: 'jbp-financial-cockpit-attention-quarter-plan-no-figures',
}

/** `q2` as the period control writes it on its own button. */
const getQuarterLabel = (quarter: CockpitPeriodKey) => quarter.toUpperCase()

/**
 * The detail of the `QX plan is not achieved` status: the product categories whose actuals came in
 * under plan in a quarter that has ENDED, worst first, with the plan and the actual beside the
 * share reached so a row can be checked rather than believed.
 *
 * The sheet states the condition as `QX is ended and Actuals < Plan`, so the quarters come from
 * the fiscal calendar rather than from the reader's period control — see the note over
 * `useEndedQuarterShortfalls`, which is where that choice is made and why.
 *
 * The status is raised on the server, and this section never re-decides it: a partner can be short
 * overall and have every category over plan under the offering and category the reader has on, and
 * the list then says what it measured instead of manufacturing a row.
 */
export const AttentionStatusQuarterPlan = () => {
  const { openSection } = useCockpitSectionNavigation()
  const metric = useSelector(selectCockpitMetric)
  const partnerData = useSelector(selectCockpitPartnerData)
  const { role } = useSelector(selectCockpitUserAccess)
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const activeOffering = useAttentionDetailOffering()
  const { fYear, currency, endedQuarters, measured, shortfalls } =
    useEndedQuarterShortfalls(activeOffering)

  const partner = partnerData[0]
  const partnerId = partner?.PartnerId ?? ''

  /* The caption names the quarters the numbers are stated over, in the words the period control
     uses for them — `Q1, Q2, Q3` — so the reader can tell at a glance that the list does not
     follow the period they have selected above. */
  const periodLabel = useMemo(() => endedQuarters.map(getQuarterLabel).join(', '), [endedQuarters])

  /* One quarter needs no column: every row belongs to it and the caption already named it. Two or
     more, and a row that does not say which quarter it is from is unreadable. */
  const withQuarterColumn = endedQuarters.length > 1

  /* The planning page reads the metric off the URL, so the cockpit's own metric is carried into
     it; `UTNet` has no planning mode of its own and falls back to the page's default. */
  const metricMode = normalizeFinancialMetricMode(metric) ?? 'net'

  /* The Fin/SOB section of the partner page, opened on one category — the path
     `PendingActionsOfferingActions` builds for `goToPlanning`, with the row's category where an
     action would have carried `resolvePlanReviewCategoryActionParam`'s. */
  const exitDetail = useAttentionDetailExit()

  const openFinSob = useCallback(
    (category?: string) => {
      scrollToElement(p360TabsIds)

      const categoryPath = category ? `/${PLANNING_SECTION_URL_SEGMENT}/${category}` : ''
      const url = `${TAB_URL}/${FINANCIAL_SECTION_URL_VALUE}${categoryPath}/${PLANNING_METRIC_URL_SEGMENT}/${metricMode}`

      openSection(getUrlParams(url))
      exitDetail()
    },
    [exitDetail, metricMode, openSection]
  )

  const handleOpenCategory = useCallback(
    (category: string) => () => openFinSob(category),
    [openFinSob]
  )

  /* The bar's own way into the section opens on the worst row — the one the reader would pick
     first — and on the offering's plan alone when nothing is short. */
  const handleGoToSection = useCallback(
    () => openFinSob(shortfalls[0]?.category),
    [openFinSob, shortfalls]
  )

  const handleOpenReview = useCallback(() => setIsReviewOpen(true), [])
  const handleCloseReview = useCallback(() => setIsReviewOpen(false), [])

  /* Continuing out of the review is the sheet's `adjust the plan` half: the justifications stay in
     the dialog, and the plan itself is changed in the section the review was reading. */
  const handleContinueReview = useCallback(() => {
    setIsReviewOpen(false)
    handleGoToSection()
  }, [handleGoToSection])

  const fyLabel = String(fYear).slice(-2)

  const columns = useMemo<ICockpitTableColumn<IQuarterPlanShortfall>[]>(
    () => [
      ...(withQuarterColumn
        ? [
            {
              key: 'quarter',
              header: <Translation span text={LOCAL_KEYS.quarter} />,
              renderCell: ({ quarter }: IQuarterPlanShortfall) => (
                <span className={styles.quarter}>{getQuarterLabel(quarter)}</span>
              ),
            },
          ]
        : []),
      {
        key: 'category',
        header: <Translation span text={REVENUE_KEYS.category} />,
        renderCell: ({ category }) => (
          <button type="button" className={styles.category} onClick={handleOpenCategory(category)}>
            <Translation span text={category} />
          </button>
        ),
      },
      {
        key: 'plan',
        header: (
          <Translation
            span
            text={REVENUE_KEYS.plan}
            replaceParams={[{ key: '{fy}', value: fyLabel }]}
          />
        ),
        renderCell: ({ plan }) => (
          <span className={styles.value}>{formatCockpitMoney(plan, currency)}</span>
        ),
      },
      {
        key: 'actuals',
        header: (
          <Translation
            span
            text={REVENUE_KEYS.actuals}
            replaceParams={[{ key: '{fy}', value: fyLabel }]}
          />
        ),
        renderCell: ({ actuals }) => (
          <span className={styles.value}>{formatCockpitMoney(actuals, currency)}</span>
        ),
      },
      {
        key: 'score',
        header: <Translation span text={KEYS.achievement} />,
        renderCell: ({ score }) => <AchievementScoreBadge score={score} />,
      },
    ],
    [withQuarterColumn, fyLabel, currency, handleOpenCategory]
  )

  /* Same as the completeness section: the list is stated per offering, and a partner showing none
     gets a sentence rather than an empty card. */
  if (!activeOffering) {
    return (
      <div className={styles.root}>
        <p className={styles.empty}>
          <Translation span text={DETAIL_KEYS.noOffering} />
        </p>
      </div>
    )
  }

  /* The one sentence that stands in for the list, in the order the reasons rule each other out: a
     year that has closed no quarter has nothing to score at all, ended quarters carrying no plan
     and no actual have nothing measured, and only then is `nothing came in short` the truth. */
  const emptyMessage = (() => {
    if (endedQuarters.length === 0) {
      return (
        <Translation
          span
          text={LOCAL_KEYS.noEndedQuarters}
          replaceParams={[{ key: '{fy}', value: fyLabel }]}
        />
      )
    }

    if (measured.length === 0) {
      return (
        <Translation
          span
          text={LOCAL_KEYS.noFigures}
          replaceParams={[{ key: '{period}', value: periodLabel }]}
        />
      )
    }

    return shortfalls.length === 0 ? (
      <Translation
        span
        text={KEYS.allAchieved}
        replaceParams={[{ key: '{period}', value: periodLabel }]}
      />
    ) : null
  })()

  return (
    <div className={styles.root}>
      {emptyMessage ? (
        <p className={styles.empty}>{emptyMessage}</p>
      ) : (
        <>
          <p className={styles.caption}>
            <Translation
              span
              text={KEYS.caption}
              replaceParams={[{ key: '{period}', value: periodLabel }]}
            />
          </p>

          <CockpitSectionHeader
            actions={
              <span className={styles.count}>
                <Translation
                  span
                  text={KEYS.count}
                  replaceParams={[
                    { key: '{count}', value: String(shortfalls.length) },
                    { key: '{total}', value: String(measured.length) },
                  ]}
                />
              </span>
            }
          >
            <Translation span text={KEYS.categories} />
          </CockpitSectionHeader>

          <div className={styles.table}>
            <CockpitTable
              columns={columns}
              items={shortfalls}
              getItemKey={({ quarter, category }) => `${quarter}:${category}`}
            />
          </div>
        </>
      )}

      {/* A justification on this status is written where every other plan justification is: the
      plan review, read on the offering the status was raised for. The review is opened for a partner,
      and the cockpit carries none until its data lands. */}
      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.quarterPlan}
        className={styles.actions}
        disabled={partnerId ? [] : ['provideJustification']}
        handlers={{
          provideJustification: handleOpenReview,
          goToSection: handleGoToSection,
        }}
      />

      <PlanReviewDialog
        show={isReviewOpen && Boolean(partnerId) && Boolean(activeOffering)}
        partnerId={partnerId}
        fYear={fYear}
        programType={activeOffering}
        isGlobal={partner?.IsGlobal === 1}
        role={PLAN_REVIEW_ROLE_BY_STATUS_ROLE[getAttentionStatusRole(role)]}
        continueCaption={ATTENTION_STATUS_ACTIONS.goToSection.labelKey}
        onClose={handleCloseReview}
        onContinue={handleContinueReview}
        onReject={handleCloseReview}
      />
    </div>
  )
}

AttentionStatusQuarterPlan.displayName = 'AttentionStatusQuarterPlan'
